import type {ThreeEvent} from "@react-three/fiber";
import * as THREE from "three";
import type {SidebarValues} from "../../App.tsx";
import type {RefObject} from "react";

/**
 * Detects zooming the object in or out.
 */
export default class ZoomGestures {

    readonly onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
    readonly onPointerMove: (e: ThreeEvent<PointerEvent>) => void;
    readonly onPointerUp: (e: ThreeEvent<PointerEvent>) => void;
    readonly onWheel: (e: ThreeEvent<WheelEvent>) => void;

    private readonly onZoom: (delta: { dx?: number; dy?: number; dz?: number }) => void
    private readonly preventPropagation: (e: Event) => void;

    private lastDistance: number | null = null;
    private wheelEndTimeout: number | null = null;

    constructor(pointers: RefObject<Map<number, { x: number; y: number }>>,
                onZoom: (delta: { dx?: number; dy?: number; dz?: number }) => void,
                setValues: SidebarValues
    ) {
        this.onZoom = onZoom

        // POINTER EVENTS @ trackpad, stylus, mouse, tablets, smartphones
        this.onPointerDown = (e: ThreeEvent<PointerEvent>) => {
            if (this.intersections(e) === 0) return;

            this.lastDistance = null;
        }

        this.onPointerMove = (e: ThreeEvent<PointerEvent>) => {
            if (this.intersections(e) === 0) return;

            if (pointers.current.size === 2 && pointers.current.has(e.pointerId)) {
                const target = (e.target as Element | null);
                try { target?.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }

                const [p1, p2] = [...pointers.current.values()];

                const dx = p1.x - p2.x;
                const dy = p1.y - p2.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (this.lastDistance) {
                    const dz = (distance - this.lastDistance) * 12;
                    this.onZoom({dz: dz});
                    setValues(prev => ({
                        ...prev,
                        "dz": dz,
                        "action": "zoom",
                        "distance": this.lastDistance ? this.lastDistance : 0,
                    }));
                }
                this.lastDistance = distance;
                pointers.current.set(e.pointerId, {x: e.clientX, y: e.clientY});
            }
        }
        this.onPointerUp = (e: ThreeEvent<PointerEvent>) => {
            const target = (e.target as Element | null);
            try { target?.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
            this.onZoom({});
            this.lastDistance = null;
        }

        // WHEEL / PINCH EVENTS @ trackpad, mouse
        this.onWheel = (e: ThreeEvent<WheelEvent>) => {
            if (this.intersections(e) === 0) return;

            //console.trace(`wheel ctrl=${e.ctrlKey}, meta=${e.metaKey}`)
            if (!e.metaKey) {
                let dz: number;
                if (e.ctrlKey) {
                    // console.trace(`zoom (pinch @ touchpad)` )
                    dz = - (e.deltaY + e.deltaX) * 18
                } else {
                    // console.trace(`zoom (mouse wheel only)`)
                    dz = e.deltaY * 5
                }
                this.onZoom({dz: dz})
                setValues(prev => ({
                    ...prev,
                    "dz": dz,
                    "action": "zoom",
                    "distance": this.lastDistance ? this.lastDistance : 0,
                }));

                // Clear existing timeout
                if (this.wheelEndTimeout !== null) {
                    window.clearTimeout(this.wheelEndTimeout);
                }

                // Set new timeout to detect end of wheel gesture
                this.wheelEndTimeout = window.setTimeout(() => {
                    // Wheel gesture ended - reset zoom
                    this.onZoom({});
                    this.wheelEndTimeout = null;
                }, 150); // 150ms without wheel events = gesture ended
            }
        }

        // disable browser propagation
        this.preventPropagation = (e: Event) => {
            e.preventDefault()
        }

        const listenerOpts = {passive: false} // allows preventDefault
        window.addEventListener('wheel', this.preventPropagation, listenerOpts);

        // console.debug("zooming gestures created")
    }

    intersections(e: THREE.Event & { intersections?: any[] }) {
        const hasIntersection = (e && e.intersections && e.intersections.length > 0);
        return hasIntersection ? e.intersections!.length : 0;
    }

    public dispose() {
        // Clear any pending wheel end timeout
        if (this.wheelEndTimeout !== null) {
            window.clearTimeout(this.wheelEndTimeout);
            this.wheelEndTimeout = null;
        }
        window.removeEventListener('wheel', this.preventPropagation);
    }
}
